import React, { useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { FiMenu, FiX, FiLogOut, FiBarChart2, FiTrendingUp, FiHeart, FiUsers, FiPlay, FiUserPlus, FiTarget, FiZap, FiActivity } from 'react-icons/fi'
import { NavBar, NavContainer, NavTitle } from './ui'
import { theme, mediaQuery } from '../theme'
import { authAPI } from '../services/api'

const MobileMenuButton = styled.button`
  display: block;
  background: none;
  border: none;
  color: white;
  font-size: 24px;
  cursor: pointer;
  padding: ${theme.spacing.sm};
  
  ${mediaQuery.tablet} {
    display: none;
  }
`

const NavMenu = styled.div`
  display: none;
  
  ${mediaQuery.tablet} {
    display: flex;
    gap: ${theme.spacing.md};
    align-items: center;
  }
`

const MobileMenu = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${theme.colors.primary.main};
  z-index: 1000;
  padding: ${theme.spacing.lg};
  transform: ${props => props.isOpen ? 'translateX(0)' : 'translateX(-100%)'};
  transition: transform 0.3s ease;
  
  ${mediaQuery.tablet} {
    display: none;
  }
`

const MobileMenuHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing.xl};
`

const MobileMenuLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.lg};
`

const NavLink = styled(Link)`
  color: white;
  text-decoration: none;
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border-radius: ${theme.borderRadius.sm};
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  font-weight: 500;
  
  &:hover {
    background: ${theme.colors.primary.dark};
  }
  
  &.active {
    background: ${theme.colors.secondary.green};
  }
  
  svg {
    font-size: 18px;
  }
`

const LogoutButton = styled.button`
  background: none;
  border: none;
  color: white;
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border-radius: ${theme.borderRadius.sm};
  cursor: pointer;
  transition: background 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  font-weight: 500;
  font-size: 16px;
  
  &:hover {
    background: ${theme.colors.status.error};
  }
  
  svg {
    font-size: 18px;
  }
`

const Navigation = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const location = useLocation()
  const navigate = useNavigate()
  
  const isActive = (path) => location.pathname === path
  
  const handleLogout = () => {
    authAPI.logout()
    navigate('/login')
  }
  
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen)
  }
  
  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false)
  }
  
  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: FiBarChart2 },
    { path: '/resilience', label: 'Resilience', icon: FiTrendingUp },
    { path: '/predictors', label: 'Predictors', icon: FiActivity },
    { path: '/optimal-pace', label: 'Optimal Pace', icon: FiZap },
    { path: '/rankings', label: 'Rankings', icon: FiUsers },
    { path: '/analysis', label: 'Analysis', icon: FiPlay },
    { path: '/plays', label: 'Plays', icon: FiTarget },
    { path: '/new-user', label: 'New User', icon: FiUserPlus },
  ]
  
  return (
    <>
      <NavBar>
        <NavContainer>
          <NavTitle>Discover</NavTitle>
          
          {/* Desktop Navigation */}
          <NavMenu>
            {navItems.map(({ path, label, icon: Icon }) => (
              <NavLink
                key={path}
                to={path}
                className={isActive(path) ? 'active' : ''}
              >
                <Icon />
                {label}
              </NavLink>
            ))}
            <LogoutButton onClick={handleLogout}>
              <FiLogOut />
              Logout
            </LogoutButton>
          </NavMenu>
          
          {/* Mobile Menu Button */}
          <MobileMenuButton onClick={toggleMobileMenu}>
            <FiMenu />
          </MobileMenuButton>
        </NavContainer>
      </NavBar>
      
      {/* Mobile Menu Overlay */}
      <MobileMenu isOpen={isMobileMenuOpen}>
        <MobileMenuHeader>
          <NavTitle>Discover</NavTitle>
          <MobileMenuButton onClick={toggleMobileMenu}>
            <FiX />
          </MobileMenuButton>
        </MobileMenuHeader>
        
        <MobileMenuLinks>
          {navItems.map(({ path, label, icon: Icon }) => (
            <NavLink
              key={path}
              to={path}
              className={isActive(path) ? 'active' : ''}
              onClick={closeMobileMenu}
            >
              <Icon />
              {label}
            </NavLink>
          ))}
          <LogoutButton onClick={handleLogout}>
            <FiLogOut />
            Logout
          </LogoutButton>
        </MobileMenuLinks>
      </MobileMenu>
    </>
  )
}

export default Navigation 